import React, { useMemo } from 'react';
import FleetWidget from 'models/fleet-management/FleetWidget';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { FleetFieldTypeLookup, getWidgetChartAxisData } from 'utils/fleet-filters';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import Box from '@mui/material/Box';
import CustomLineChart from 'components/data-widgets/CustomLineChart';
import { createFleetAxisTooltipContent } from './FleetChartTooltip';

const LineChartWidget: React.FC<{
  widget: FleetWidget;
  data: any[] | null;
  getStatus: HttpFetchStatus;
  width: number;
  height: number;
  fieldTypes?: FleetFieldTypeLookup;
}> = ({ widget, data, width, height, getStatus, fieldTypes }) => {
  const { t } = useTranslation();

  const axisData = useMemo(
    () => getWidgetChartAxisData(widget, data, fieldTypes),
    [widget, data, fieldTypes],
  );

  const axisContent = useMemo(
    () => createFleetAxisTooltipContent(axisData.groupMeta),
    [axisData.groupMeta],
  );

  if (
    widget.fields == null ||
    widget.fields.length < 1 ||
    widget.groups == null ||
    widget.groups.length < 1
  ) {
    return <CustomAlert header={t('error-chart-incorrectly-configured')} severity="error" />;
  }

  return (
    <React.Fragment>
      <Box sx={{ height, width, overflow: 'hidden' }}>
        <CustomLineChart
          loading={getStatus === HttpFetchStatus.Loading}
          parentHeight
          tooltip={{ trigger: 'axis', slots: { axisContent } }}
          xAxis={[
            {
              data: axisData.groups,
              label: widget.groups?.map((g) => t('fleet-management-field-' + g)).join(' | '),
              scaleType: 'band',
            },
          ]}
          series={axisData.datasets.map((ds) => ({
            data: ds.data,
            label: ds.label,
          }))}
          yLabel="Values"
        />
      </Box>
    </React.Fragment>
  );
};

export default LineChartWidget;
